import { useState, useEffect, useCallback, useRef } from 'react'
import {
  apiPayerDirectoryList,
  apiPayerDirectorySearch,
  apiPayerDirectoryDetail,
  apiPayerDirectoryCoverSheet,
} from './api'
import type { PayerCarrierSummary, PayerCarrierDetail, PayerCoverSheet } from './api'

// ── Color palette for carrier icons ─────────────────────────────────────────
const CARRIER_COLORS: Record<string, string> = {
  aetna: '#7B2D8E',
  unitedhealthcare: '#002677',
  cigna: '#E17000',
  bcbs: '#0072CE',
  humana: '#53B848',
  elevance: '#004C97',
  molina: '#00A5DF',
  kaiser: '#003B71',
  centene: '#00836C',
  cvs_caremark: '#CC0000',
  medicare: '#003366',
  medicaid: '#2E7D32',
  tricare: '#003A70',
  highmark: '#0067A5',
  florida_blue: '#0057B8',
  premera: '#006BA6',
}

function getCarrierColor(key: string): string {
  return CARRIER_COLORS[key] || '#0d9488'
}

function getCarrierInitial(name: string): string {
  if (name.startsWith('Blue')) return 'BC'
  if (name.startsWith('United')) return 'UH'
  if (name.startsWith('CVS')) return 'CV'
  if (name.includes('Medicare')) return 'MC'
  if (name.includes('Medicaid')) return 'MA'
  if (name.includes('TRICARE')) return 'TR'
  if (name.includes('Centene')) return 'CE'
  if (name.includes('Elevance')) return 'EH'
  return name.charAt(0).toUpperCase()
}

interface Props {
  onClose: () => void
  doctorName?: string
  doctorFax?: string
}

export default function PayerDirectory({ onClose, doctorName, doctorFax }: Props) {
  const [carriers, setCarriers] = useState<PayerCarrierSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedCarrier, setExpandedCarrier] = useState<string | null>(null)
  const [carrierDetail, setCarrierDetail] = useState<PayerCarrierDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [showCoverSheet, setShowCoverSheet] = useState(false)
  const [coverSheet, setCoverSheet] = useState<PayerCoverSheet | null>(null)
  const [coverSheetLoading, setCoverSheetLoading] = useState(false)
  const [coverSheetDrug, setCoverSheetDrug] = useState('')
  const [coverSheetPatientRef, setCoverSheetPatientRef] = useState('')
  const [coverSheetAppealType, setCoverSheetAppealType] = useState<'medical' | 'pharmacy'>('medical')
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    loadCarriers()
  }, [])

  async function loadCarriers() {
    setLoading(true)
    try {
      const data = await apiPayerDirectoryList()
      setCarriers(data.carriers)
    } catch {
      // silent fail
    }
    setLoading(false)
  }

  const handleSearch = useCallback((q: string) => {
    setSearchQuery(q)
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(async () => {
      if (!q.trim()) {
        loadCarriers()
        return
      }
      setLoading(true)
      try {
        const data = await apiPayerDirectorySearch(q.trim())
        setCarriers(data.carriers)
      } catch { }
      setLoading(false)
    }, 250)
  }, [])

  async function handleExpand(key: string) {
    if (expandedCarrier === key) {
      setExpandedCarrier(null)
      setCarrierDetail(null)
      return
    }
    setExpandedCarrier(key)
    setDetailLoading(true)
    try {
      const detail = await apiPayerDirectoryDetail(key)
      setCarrierDetail(detail)
    } catch { }
    setDetailLoading(false)
  }

  function copyToClipboard(text: string, label: string) {
    navigator.clipboard.writeText(text)
    setToast(`${label} copied!`)
    setTimeout(() => setToast(null), 2000)
  }

  async function handleGenerateCoverSheet(carrierKey: string) {
    setCoverSheetLoading(true)
    try {
      const data = await apiPayerDirectoryCoverSheet(carrierKey, {
        doctor_name: doctorName,
        doctor_fax: doctorFax,
        drug_name: coverSheetDrug || undefined,
        patient_ref: coverSheetPatientRef || undefined,
        appeal_type: coverSheetAppealType,
      })
      setCoverSheet(data)
      setShowCoverSheet(true)
    } catch { }
    setCoverSheetLoading(false)
  }

  function handlePrintCoverSheet() {
    window.print()
  }

  // Keyboard: Escape closes
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (showCoverSheet) { setShowCoverSheet(false); return }
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, showCoverSheet])

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center print:bg-white print:backdrop-blur-none print:static">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col print:shadow-none print:rounded-none print:max-h-none print:max-w-none print:mx-0">

        {/* Header — hidden when printing cover sheet */}
        <div className="print:hidden sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-teal-500 flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Payer Contact Directory</h2>
              <p className="text-xs text-slate-400">Appeals fax numbers, phones, P2P scheduling, and cover sheets</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search bar — hidden when printing */}
        <div className="print:hidden px-6 py-3 border-b border-slate-100">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
              placeholder="Search carriers (e.g., Aetna, UnitedHealthcare, Medicare...)"
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
              autoFocus
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
            </div>
          ) : carriers.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <p className="text-lg font-semibold">No carriers found</p>
              <p className="text-sm mt-1">Try a different search term</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {carriers.map(carrier => (
                <div key={carrier.key} className="border border-slate-200 rounded-xl overflow-hidden hover:border-teal-300 transition-colors">
                  {/* Card header */}
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                        style={{ backgroundColor: getCarrierColor(carrier.key) }}
                      >
                        {getCarrierInitial(carrier.carrier_name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-sm text-slate-800 truncate">{carrier.carrier_name}</h3>
                        <p className="text-xs text-slate-400 truncate">{carrier.parent_company}</p>
                      </div>
                    </div>

                    {/* Quick-access buttons */}
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {carrier.appeals_fax && !carrier.appeals_fax.startsWith('Varies') && !carrier.appeals_fax.startsWith('N/A') && (
                        <button
                          onClick={(e) => { e.stopPropagation(); copyToClipboard(carrier.appeals_fax, 'Appeals fax') }}
                          className="flex items-center gap-1 text-[11px] font-semibold bg-red-50 text-red-600 border border-red-200 rounded-full px-2.5 py-1 hover:bg-red-100 transition-colors"
                        >
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18.75 9H5.25" />
                          </svg>
                          Fax
                        </button>
                      )}
                      {carrier.appeals_phone && !carrier.appeals_phone.startsWith('Varies') && !carrier.appeals_phone.startsWith('N/A') && (
                        <button
                          onClick={(e) => { e.stopPropagation(); copyToClipboard(carrier.appeals_phone, 'Appeals phone') }}
                          className="flex items-center gap-1 text-[11px] font-semibold bg-blue-50 text-blue-600 border border-blue-200 rounded-full px-2.5 py-1 hover:bg-blue-100 transition-colors"
                        >
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                          </svg>
                          Phone
                        </button>
                      )}
                      {carrier.peer_to_peer_phone && !carrier.peer_to_peer_phone.startsWith('Varies') && (
                        <button
                          onClick={(e) => { e.stopPropagation(); copyToClipboard(carrier.peer_to_peer_phone, 'P2P phone') }}
                          className="flex items-center gap-1 text-[11px] font-semibold bg-violet-50 text-violet-600 border border-violet-200 rounded-full px-2.5 py-1 hover:bg-violet-100 transition-colors"
                        >
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                          </svg>
                          P2P
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleExpand(carrier.key) }}
                        className="flex items-center gap-1 text-[11px] font-semibold bg-slate-50 text-slate-500 border border-slate-200 rounded-full px-2.5 py-1 hover:bg-slate-100 transition-colors"
                      >
                        <svg className={`w-3 h-3 transition-transform ${expandedCarrier === carrier.key ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                        {expandedCarrier === carrier.key ? 'Less' : 'Details'}
                      </button>
                    </div>
                  </div>

                  {/* Expanded detail section */}
                  {expandedCarrier === carrier.key && (
                    <div className="border-t border-slate-100 bg-slate-50 px-4 py-3 space-y-3 text-xs">
                      {detailLoading ? (
                        <div className="flex items-center justify-center py-4">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-teal-500"></div>
                        </div>
                      ) : carrierDetail ? (
                        <>
                          {/* Medical Appeals */}
                          <div>
                            <p className="font-bold text-slate-600 uppercase tracking-wide text-[10px] mb-1">Medical Appeals</p>
                            <div className="bg-white rounded-lg p-2.5 space-y-1 border border-slate-200">
                              <div className="flex justify-between"><span className="text-slate-400">Fax:</span><span className="font-semibold text-slate-700">{carrierDetail.appeals.medical.fax}</span></div>
                              <div className="flex justify-between"><span className="text-slate-400">Phone:</span><span className="font-semibold text-slate-700">{carrierDetail.appeals.medical.phone}</span></div>
                              <div><span className="text-slate-400">Address: </span><span className="text-slate-600">{carrierDetail.appeals.medical.mailing_address}</span></div>
                              <div className="flex justify-between"><span className="text-slate-400">Hours:</span><span className="text-slate-600">{carrierDetail.appeals.medical.hours}</span></div>
                              <div><span className="text-slate-400">Turnaround: </span><span className="text-slate-600">{carrierDetail.appeals.medical.turnaround}</span></div>
                            </div>
                          </div>

                          {/* Pharmacy Appeals */}
                          <div>
                            <p className="font-bold text-slate-600 uppercase tracking-wide text-[10px] mb-1">Pharmacy Appeals</p>
                            <div className="bg-white rounded-lg p-2.5 space-y-1 border border-slate-200">
                              <div className="flex justify-between"><span className="text-slate-400">Fax:</span><span className="font-semibold text-slate-700">{carrierDetail.appeals.pharmacy.fax}</span></div>
                              <div className="flex justify-between"><span className="text-slate-400">Phone:</span><span className="font-semibold text-slate-700">{carrierDetail.appeals.pharmacy.phone}</span></div>
                              <div><span className="text-slate-400">Address: </span><span className="text-slate-600">{carrierDetail.appeals.pharmacy.mailing_address}</span></div>
                              <div className="flex justify-between"><span className="text-slate-400">Hours:</span><span className="text-slate-600">{carrierDetail.appeals.pharmacy.hours}</span></div>
                              <div><span className="text-slate-400">Turnaround: </span><span className="text-slate-600">{carrierDetail.appeals.pharmacy.turnaround}</span></div>
                            </div>
                          </div>

                          {/* Peer-to-Peer */}
                          <div>
                            <p className="font-bold text-slate-600 uppercase tracking-wide text-[10px] mb-1">Peer-to-Peer Review</p>
                            <div className="bg-white rounded-lg p-2.5 space-y-1 border border-slate-200">
                              <div className="flex justify-between"><span className="text-slate-400">Phone:</span><span className="font-semibold text-slate-700">{carrierDetail.peer_to_peer.phone}</span></div>
                              <div><span className="text-slate-400">Wait: </span><span className="text-slate-600">{carrierDetail.peer_to_peer.typical_wait}</span></div>
                              <p className="text-slate-500 mt-1">{carrierDetail.peer_to_peer.scheduling_notes}</p>
                            </div>
                          </div>

                          {/* Prior Auth */}
                          <div>
                            <p className="font-bold text-slate-600 uppercase tracking-wide text-[10px] mb-1">Prior Authorization</p>
                            <div className="bg-white rounded-lg p-2.5 space-y-1 border border-slate-200">
                              <div className="flex justify-between"><span className="text-slate-400">Phone:</span><span className="font-semibold text-slate-700">{carrierDetail.prior_auth.phone}</span></div>
                              <div className="flex justify-between"><span className="text-slate-400">Fax:</span><span className="font-semibold text-slate-700">{carrierDetail.prior_auth.fax}</span></div>
                              <div className="flex justify-between">
                                <span className="text-slate-400">Portal:</span>
                                <a href={carrierDetail.prior_auth.portal} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline truncate max-w-[200px]">{carrierDetail.prior_auth.portal}</a>
                              </div>
                            </div>
                          </div>

                          {/* Provider Relations */}
                          <div>
                            <p className="font-bold text-slate-600 uppercase tracking-wide text-[10px] mb-1">Provider Relations</p>
                            <div className="bg-white rounded-lg p-2.5 space-y-1 border border-slate-200">
                              <div className="flex justify-between"><span className="text-slate-400">Phone:</span><span className="font-semibold text-slate-700">{carrierDetail.provider_relations.phone}</span></div>
                              <div className="flex justify-between">
                                <span className="text-slate-400">Portal:</span>
                                <a href={carrierDetail.provider_relations.portal} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline truncate max-w-[200px]">{carrierDetail.provider_relations.portal}</a>
                              </div>
                            </div>
                          </div>

                          {/* State Filing Notes */}
                          {carrierDetail.state_filing_contacts?.notes && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                              <p className="font-bold text-amber-700 text-[10px] uppercase tracking-wide mb-1">Filing Notes</p>
                              <p className="text-amber-700">{carrierDetail.state_filing_contacts.notes}</p>
                            </div>
                          )}

                          {/* Fax Cover Sheet Generator */}
                          <div className="pt-1">
                            <p className="font-bold text-slate-600 uppercase tracking-wide text-[10px] mb-2">Generate Fax Cover Sheet</p>
                            <div className="bg-white rounded-lg p-3 border border-slate-200 space-y-2">
                              <div className="grid grid-cols-2 gap-2">
                                <input
                                  type="text"
                                  value={coverSheetDrug}
                                  onChange={e => setCoverSheetDrug(e.target.value)}
                                  placeholder="Drug name (optional)"
                                  className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                                />
                                <input
                                  type="text"
                                  value={coverSheetPatientRef}
                                  onChange={e => setCoverSheetPatientRef(e.target.value)}
                                  placeholder="Reference tag (optional)"
                                  className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                                />
                              </div>
                              <div className="flex items-center gap-3">
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                  <input type="radio" name={`appeal-type-${carrier.key}`} checked={coverSheetAppealType === 'medical'} onChange={() => setCoverSheetAppealType('medical')} className="accent-teal-500" />
                                  <span className="text-slate-600">Medical</span>
                                </label>
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                  <input type="radio" name={`appeal-type-${carrier.key}`} checked={coverSheetAppealType === 'pharmacy'} onChange={() => setCoverSheetAppealType('pharmacy')} className="accent-teal-500" />
                                  <span className="text-slate-600">Pharmacy</span>
                                </label>
                                <button
                                  onClick={() => handleGenerateCoverSheet(carrier.key)}
                                  disabled={coverSheetLoading}
                                  className="ml-auto px-3 py-1.5 bg-teal-500 text-white font-semibold rounded-lg hover:bg-teal-600 disabled:opacity-50 transition-colors"
                                >
                                  {coverSheetLoading ? 'Generating...' : 'Generate Cover Sheet'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <p className="text-slate-400 text-center py-4">Failed to load details</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Toast notification */}
        {toast && (
          <div className="print:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-slate-800 text-white text-sm font-semibold px-4 py-2.5 rounded-xl shadow-lg animate-fade-in">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {toast}
            </div>
          </div>
        )}
      </div>

      {/* ── Fax Cover Sheet Modal ── */}
      {showCoverSheet && coverSheet && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center print:bg-white print:static">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 print:shadow-none print:rounded-none print:max-w-none print:mx-0">
            {/* Print-only: cover sheet content */}
            <div className="p-8 print:p-12">
              {/* Print controls — hidden in print */}
              <div className="print:hidden flex items-center justify-between mb-6">
                <h3 className="font-bold text-slate-800">Fax Cover Sheet Preview</h3>
                <div className="flex items-center gap-2">
                  <button onClick={handlePrintCoverSheet}
                    className="px-4 py-2 bg-teal-500 text-white text-xs font-semibold rounded-lg hover:bg-teal-600 transition-colors flex items-center gap-1.5">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18.75 9H5.25" />
                    </svg>
                    Print
                  </button>
                  <button onClick={() => setShowCoverSheet(false)} className="text-slate-400 hover:text-slate-600">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Cover sheet content */}
              <div className="border-2 border-slate-800 p-6 space-y-4 text-sm print:border-black">
                <div className="text-center border-b-2 border-slate-800 pb-4 print:border-black">
                  <h1 className="text-2xl font-bold tracking-wide">FAX COVER SHEET</h1>
                  <p className="text-xs text-slate-500 mt-1 print:text-black">CONFIDENTIAL — APPEAL CORRESPONDENCE</p>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="space-y-3">
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase print:text-black">To</p>
                      <p className="font-bold text-slate-800 print:text-black">{coverSheet.to}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase print:text-black">Fax Number</p>
                      <p className="font-bold text-slate-800 text-lg print:text-black">{coverSheet.to_fax}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase print:text-black">Address</p>
                      <p className="text-slate-600 print:text-black">{coverSheet.to_address}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase print:text-black">From</p>
                      <p className="font-bold text-slate-800 print:text-black">{coverSheet.from_name}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase print:text-black">Fax Number</p>
                      <p className="font-bold text-slate-800 print:text-black">{coverSheet.from_fax}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase print:text-black">Date</p>
                      <p className="text-slate-600 print:text-black">{coverSheet.date}</p>
                    </div>
                  </div>
                </div>

                <div className="border-t-2 border-slate-300 pt-3 mt-3 space-y-3 print:border-black">
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase print:text-black">RE</p>
                    <p className="font-bold text-slate-800 text-base print:text-black">{coverSheet.re}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase print:text-black">Pages (including cover)</p>
                    <div className="border-b border-slate-300 w-20 mt-1 print:border-black">&nbsp;</div>
                  </div>
                </div>

                <div className="border-t-2 border-slate-300 pt-3 mt-3 print:border-black">
                  <p className="text-[10px] font-bold text-slate-400 uppercase print:text-black">Notes</p>
                  <div className="h-32 border border-slate-200 rounded mt-1 print:border-black"></div>
                </div>

                <p className="text-[9px] text-slate-400 text-center pt-2 print:text-black">
                  This fax and any attachments are confidential and may contain protected health information (PHI).
                  If you are not the intended recipient, please notify the sender immediately and destroy all copies.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
